import { useQuery } from '@tanstack/react-query';
import type { CommissionResponse, WithdrawType } from '@/shared/types';
import { getWithdrawCommission } from '@/entities/operation';
import { CACHE_TIME_10_MINUTES, CACHE_TIME_5_MINUTES } from '@/shared/config';

interface UseDepositCommissionOptions<
  TParams = Record<string, string | number>,
> {
  operationType: WithdrawType;
  params: TParams;
  enabled?: boolean;
  queryKey?: string[];
  staleTime?: number;
  gcTime?: number;
  retry?: number;
}

export function useWithdrawCommission<
  TResponse extends CommissionResponse = CommissionResponse,
  TParams extends Record<string, string | number> = Record<
    string,
    string | number
  >,
>({
  operationType,
  params,
  enabled = true,
  queryKey,
  staleTime = CACHE_TIME_5_MINUTES,
  gcTime = CACHE_TIME_10_MINUTES,
}: UseDepositCommissionOptions<TParams>) {
  const defaultQueryKey = [
    `${operationType}WithdrawCommission`,
    ...Object.values(params),
  ];
  const finalQueryKey = queryKey || defaultQueryKey;

  const query = useQuery<TResponse, Error>({
    queryKey: finalQueryKey,
    queryFn: async () => {
      return await getWithdrawCommission<TResponse, TParams>(
        operationType,
        params
      );
    },
    enabled,
    staleTime,
    gcTime,
    retry: 0,
  });

  return {
    commissionData: query.data,
    commissionError: query.error,
    isCommissionFetching: query.isFetching,
    query,
  };
}
